import { createHash } from "node:crypto";
import {
  getBase64Encoder,
  getTransactionDecoder,
  getCompiledTransactionMessageDecoder,
  getProgramDerivedAddress,
  getAddressEncoder,
  type Transaction,
  type Address,
  type Instruction,
  createSolanaRpc,
  devnet,
  testnet,
  mainnet,
  type RpcDevnet,
  type SolanaRpcApiDevnet,
  type RpcTestnet,
  type SolanaRpcApiTestnet,
  type RpcMainnet,
  type SolanaRpcApiMainnet,
} from "@solana/kit";
import { TOKEN_PROGRAM_ADDRESS } from "@solana-program/token";
import { TOKEN_2022_PROGRAM_ADDRESS } from "@solana-program/token-2022";
import type { Network } from "@payai/x402/types";
import {
  SVM_ADDRESS_REGEX,
  DEVNET_RPC_URL,
  TESTNET_RPC_URL,
  MAINNET_RPC_URL,
  USDC_MAINNET_ADDRESS,
  USDC_DEVNET_ADDRESS,
  USDC_TESTNET_ADDRESS,
  SOLANA_MAINNET_CAIP2,
  SOLANA_DEVNET_CAIP2,
  SOLANA_TESTNET_CAIP2,
  V1_TO_V2_NETWORK_MAP,
  COMPUTE_BUDGET_PROGRAM_ADDRESS,
  SWIG_PROGRAM_ADDRESS,
  SWIG_SIGN_V2_DISCRIMINATOR,
  SECP256R1_PRECOMPILE_ADDRESS,
} from "./constants";
import type { ExactSvmPayloadV1 } from "./types";

/**
 * Normalize network identifier to CAIP-2 format
 * Handles both V1 names (solana, solana-devnet) and V2 CAIP-2 format
 *
 * @param network - Network identifier (V1 or V2 format)
 * @returns CAIP-2 network identifier
 */
export function normalizeNetwork(network: Network): string {
  // If it's already CAIP-2 format (contains ":"), validate it's supported
  if (network.includes(":")) {
    const supported = [SOLANA_MAINNET_CAIP2, SOLANA_DEVNET_CAIP2, SOLANA_TESTNET_CAIP2];
    if (!supported.includes(network)) {
      throw new Error(`Unsupported SVM network: ${network}`);
    }
    return network;
  }

  // Otherwise, it's a V1 network name, convert to CAIP-2
  const caip2Network = V1_TO_V2_NETWORK_MAP[network];
  if (!caip2Network) {
    throw new Error(`Unsupported SVM network: ${network}`);
  }
  return caip2Network;
}

/**
 * Validate Solana address format
 *
 * @param address - Base58 encoded address string
 * @returns true if address is valid, false otherwise
 */
export function validateSvmAddress(address: string): boolean {
  return SVM_ADDRESS_REGEX.test(address);
}

/**
 * Compute a stable, immutable cache key for a decoded transaction by hashing its
 * message bytes. The fee-payer signature (slot 0) is overwritten by the facilitator
 * before broadcast, so an attacker can randomize those bytes to bypass a wire-bytes
 * cache key. The message is what every signer commits to, making its hash a reliable
 * payment identity.
 *
 * @param transaction - Decoded transaction whose message bytes to hash
 * @returns Base64-encoded SHA-256 hash of the transaction message bytes
 */
export function transactionMessageHash(transaction: Transaction): string {
  return createHash("sha256").update(Buffer.from(transaction.messageBytes)).digest("base64");
}

/**
 * Decode a base64 encoded transaction from an SVM payload
 *
 * @param svmPayload - The SVM payload containing a base64 encoded transaction
 * @returns Decoded Transaction object
 */
export function decodeTransactionFromPayload(svmPayload: ExactSvmPayloadV1): Transaction {
  try {
    const base64Encoder = getBase64Encoder();
    const transactionBytes = base64Encoder.encode(svmPayload.transaction);
    const transactionDecoder = getTransactionDecoder();
    return transactionDecoder.decode(transactionBytes);
  } catch (error) {
    console.error("Error decoding transaction:", error);
    throw new Error("invalid_exact_svm_payload_transaction");
  }
}

/**
 * Extract the token sender (owner of the source token account) from a TransferChecked instruction
 *
 * @param transaction - The decoded transaction
 * @returns The token payer address as a base58 string
 */
export function getTokenPayerFromTransaction(transaction: Transaction): string {
  const compiled = getCompiledTransactionMessageDecoder().decode(transaction.messageBytes);
  const staticAccounts = compiled.staticAccounts ?? [];
  const instructions = compiled.instructions ?? [];

  for (const ix of instructions) {
    const programIndex = ix.programAddressIndex;
    const programAddress = staticAccounts[programIndex].toString();

    // Check if this is a token program instruction
    if (
      programAddress === TOKEN_PROGRAM_ADDRESS.toString() ||
      programAddress === TOKEN_2022_PROGRAM_ADDRESS.toString()
    ) {
      const accountIndices: number[] = ix.accountIndices ?? [];
      // TransferChecked account order: [source, mint, destination, owner, ...]
      if (accountIndices.length >= 4) {
        const ownerIndex = accountIndices[3];
        const ownerAddress = staticAccounts[ownerIndex].toString();
        if (ownerAddress) return ownerAddress;
      }
    }
  }

  return "";
}

/**
 * Create an RPC client for the specified network
 *
 * @param network - Network identifier (CAIP-2 or V1 format)
 * @param customRpcUrl - Optional custom RPC URL
 * @returns RPC client for the specified network
 */
export function createRpcClient(
  network: Network,
  customRpcUrl?: string,
):
  | RpcDevnet<SolanaRpcApiDevnet>
  | RpcTestnet<SolanaRpcApiTestnet>
  | RpcMainnet<SolanaRpcApiMainnet> {
  const caip2Network = normalizeNetwork(network);

  switch (caip2Network) {
    case SOLANA_DEVNET_CAIP2: {
      const url = customRpcUrl || DEVNET_RPC_URL;
      return createSolanaRpc(devnet(url)) as RpcDevnet<SolanaRpcApiDevnet>;
    }
    case SOLANA_TESTNET_CAIP2: {
      const url = customRpcUrl || TESTNET_RPC_URL;
      return createSolanaRpc(testnet(url)) as RpcTestnet<SolanaRpcApiTestnet>;
    }
    case SOLANA_MAINNET_CAIP2: {
      const url = customRpcUrl || MAINNET_RPC_URL;
      return createSolanaRpc(mainnet(url)) as RpcMainnet<SolanaRpcApiMainnet>;
    }
    default:
      throw new Error(`Unsupported network: ${network}`);
  }
}

/**
 * Get the default USDC mint address for a network
 *
 * @param network - Network identifier (CAIP-2 or V1 format)
 * @returns USDC mint address for the network
 */
export function getUsdcAddress(network: Network): string {
  const caip2Network = normalizeNetwork(network);

  switch (caip2Network) {
    case SOLANA_MAINNET_CAIP2:
      return USDC_MAINNET_ADDRESS;
    case SOLANA_DEVNET_CAIP2:
      return USDC_DEVNET_ADDRESS;
    case SOLANA_TESTNET_CAIP2:
      return USDC_TESTNET_ADDRESS;
    default:
      throw new Error(`No USDC address configured for network: ${network}`);
  }
}

// Re-export from core for backward compatibility (single implementation in @payai/x402)
export { convertToTokenAmount, numberToDecimalString } from "@payai/x402/utils";

// ─── Swig wallet support ──────────────────────────────────────────────────────

/**
 * A decoded compact instruction extracted from a Swig SignV2 payload.
 * Indices reference the SignV2 instruction's own account list, not the outer
 * transaction's static account keys.
 */
export interface SwigCompactInstruction {
  programIdIndex: number;
  accounts: number[];
  data: Uint8Array;
}

/**
 * Returns true when the transaction has a Swig layout:
 *   - Every instruction is one of: compute budget, secp256r1 precompile, or Swig SignV2
 *   - At least one Swig SignV2 instruction is present
 *
 * @param instructions - Decompiled instruction array
 * @returns True when the instruction list matches the Swig transaction shape.
 */
export function isSwigTransaction(instructions: ReadonlyArray<Instruction>): boolean {
  if (instructions.length === 0) return false;

  let hasSignV2 = false;
  for (const ix of instructions) {
    const addr = ix.programAddress.toString();
    if (addr === COMPUTE_BUDGET_PROGRAM_ADDRESS || addr === SECP256R1_PRECOMPILE_ADDRESS) continue;
    if (addr === SWIG_PROGRAM_ADDRESS) {
      const data = ix.data;
      if (!data || data.length < 2) return false;
      const discriminator = data[0] | (data[1] << 8); // U16 LE
      if (discriminator !== SWIG_SIGN_V2_DISCRIMINATOR) return false;
      hasSignV2 = true;
      continue;
    }
    return false; // unrecognized instruction
  }
  return hasSignV2;
}

/**
 * Flatten a Swig transaction into the same instruction layout as a regular one.
 * Collects non-precompile, non-SignV2 outer instructions (compute budgets) and
 * resolves the compact instructions embedded in each SignV2 instruction.
 *
 * A transaction may contain multiple SignV2 instructions. All must reference
 * the same Swig PDA (accounts[0]).
 *
 * @param instructions  - Decompiled instruction array (from a Swig transaction)
 * @param _ - Ordered account list from the compiled transaction message. Swig parsing
 *   currently resolves accounts from the SignV2 instruction payload instead.
 * @returns Object with flattened `instructions` array and `swigPda` address
 */
export async function parseSwigTransaction(
  instructions: ReadonlyArray<Instruction>,
  _: ReadonlyArray<Address>,
): Promise<{
  instructions: Array<{
    programAddress: Address;
    accounts: Array<{ address: Address; role: number }>;
    data: Uint8Array;
  }>;
  swigPda: string;
}> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any[] = [];
  const signV2Instructions: Instruction[] = [];

  // 1. Single pass: separate SignV2 instructions from the rest
  for (const ix of instructions) {
    const addr = ix.programAddress.toString();
    if (addr === SECP256R1_PRECOMPILE_ADDRESS) continue; // skip precompile
    if (addr === SWIG_PROGRAM_ADDRESS) {
      signV2Instructions.push(ix);
    } else {
      result.push(ix); // compute budget and other non-precompile instructions
    }
  }

  // Sort compute budget instructions so SetComputeUnitLimit (disc=2) precedes
  // SetComputeUnitPrice (disc=3), matching the order the facilitator expects.
  result.sort((a, b) => {
    const aIsCB = a.programAddress.toString() === COMPUTE_BUDGET_PROGRAM_ADDRESS;
    const bIsCB = b.programAddress.toString() === COMPUTE_BUDGET_PROGRAM_ADDRESS;
    if (aIsCB && bIsCB && a.data?.length > 0 && b.data?.length > 0) {
      return a.data[0] - b.data[0];
    }
    return 0;
  });

  if (signV2Instructions.length === 0) {
    throw new Error("invalid_exact_svm_payload_no_transfer_instruction");
  }

  // 2. Process each SignV2 instruction
  let swigPda = "";
  const addressEncoder = getAddressEncoder();

  for (const signV2Ix of signV2Instructions) {
    // Extract Swig PDA from SignV2's first account
    const pda = signV2Ix.accounts?.[0]?.address?.toString() ?? "";
    if (!pda) throw new Error("invalid_exact_svm_payload_no_transfer_instruction");

    // Enforce all SignV2 instructions share the same PDA
    if (swigPda === "") {
      swigPda = pda;
    } else if (pda !== swigPda) {
      throw new Error(
        "swig_pda_mismatch: all SignV2 instructions must reference the same Swig PDA",
      );
    }

    // Validate Swig wallet address derivation (cross-check accounts[0] and accounts[1])
    const swigWalletAddress = signV2Ix.accounts?.[1]?.address?.toString() ?? "";
    if (!swigWalletAddress) throw new Error("invalid_swig_wallet_address_derivation");

    const [expectedWalletAddress] = await getProgramDerivedAddress({
      programAddress: SWIG_PROGRAM_ADDRESS as Address,
      seeds: [
        new TextEncoder().encode("swig-wallet-address"),
        addressEncoder.encode(pda as Address),
      ],
    });

    if (swigWalletAddress !== expectedWalletAddress.toString()) {
      throw new Error("invalid_swig_wallet_address_derivation");
    }

    // Decode compact instructions from SignV2 data
    const rawData = signV2Ix.data ? new Uint8Array(signV2Ix.data) : new Uint8Array(0);
    const compactInstructions = decodeSwigCompactInstructions(rawData);

    // Resolve compact instruction indices through signV2's account list
    const signV2Accounts = signV2Ix.accounts ?? [];
    for (const ci of compactInstructions) {
      if (ci.programIdIndex >= signV2Accounts.length) {
        throw new Error(
          `compact instruction programIdIndex ${ci.programIdIndex} out of range (signV2 has ${signV2Accounts.length} accounts)`,
        );
      }
      result.push({
        programAddress: signV2Accounts[ci.programIdIndex].address as Address,
        accounts: ci.accounts.map(idx => {
          if (idx >= signV2Accounts.length) {
            throw new Error(
              `compact instruction account index ${idx} out of range (signV2 has ${signV2Accounts.length} accounts)`,
            );
          }
          return { address: signV2Accounts[idx].address as Address, role: 1 };
        }),
        data: ci.data,
      });
    }
  }

  return { instructions: result, swigPda };
}

/**
 * Decode the compact instructions embedded inside a Swig SignV2 instruction.
 *
 * Layout of the outer instruction data:
 *   [0..1]  discriminator         U16 LE
 *   [2..3]  instructionPayloadLen U16 LE (byte count of compact instructions)
 *   [4..7]  roleId                U32 LE
 *   [8..]   compact instructions  (instructionPayloadLen bytes)
 *
 * Compact instructions payload:
 *   [0]       numInstructions U8
 *   [1..]     compact instruction entries...
 *
 * Each CompactInstruction:
 *   [0]       programIdIndex U8
 *   [1]       numAccounts    U8
 *   [2..N+1]  accounts       U8[numAccounts]
 *   [N+2..N+3] dataLen       U16 LE
 *   [N+4..]   data           raw bytes
 *
 * @param data - The full instruction data bytes of the outer Swig instruction
 * @returns Array of decoded compact instructions (may be empty if data is malformed)
 */
export function decodeSwigCompactInstructions(data: Uint8Array): SwigCompactInstruction[] {
  if (data.length < 4) {
    throw new Error(`swig instruction data too short: need ≥4 bytes, got ${data.length}`);
  }

  // instructionPayloadLen at bytes 2-3 (U16 LE)
  const instructionPayloadLen = data[2] | (data[3] << 8);

  // Compact instructions start at byte 8 (after discriminator + payloadLen + roleId)
  const startOffset = 8;
  if (data.length < startOffset + instructionPayloadLen) {
    throw new Error(
      `swig instruction data truncated: payload needs ${instructionPayloadLen} bytes but only ${data.length - startOffset} available after offset ${startOffset}`,
    );
  }

  const results: SwigCompactInstruction[] = [];
  let offset = startOffset + 1; // skip numInstructions count byte
  const endOffset = startOffset + instructionPayloadLen;

  while (offset < endOffset) {
    if (offset >= data.length) break;

    // programIdIndex: U8
    const programIdIndex = data[offset];
    offset += 1;

    // numAccounts: U8
    if (offset >= endOffset) break;
    const numAccounts = data[offset];
    offset += 1;

    // accounts: U8[numAccounts]
    if (offset + numAccounts > endOffset) break;
    const accounts = Array.from(data.slice(offset, offset + numAccounts));
    offset += numAccounts;

    // dataLen: U16 LE
    if (offset + 2 > endOffset) break;
    const dataLen = data[offset] | (data[offset + 1] << 8);
    offset += 2;

    // instruction data
    if (offset + dataLen > endOffset) break;
    const instrData = new Uint8Array(data.slice(offset, offset + dataLen));
    offset += dataLen;

    results.push({ programIdIndex, accounts, data: instrData });
  }

  return results;
}
